import React, { useEffect, useState } from "react";

import BugReportIcon from "@mui/icons-material/BugReport";
import ErrorIcon from "@mui/icons-material/Error";
import HomeIcon from "@mui/icons-material/Home";
import LoginIcon from "@mui/icons-material/Login";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box,
  Typography,
  Button,
  Alert,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router";

import {
  RECOVERY,
  ORGANIZATIONS,
  AUTH,
} from "../../../platform/constants/routes";
import { EventBus } from "../../../platform/events/EventBus";
import { StateEvent } from "../../../platform/state/types";

interface ErrorInfo {
  code?: string;
  message: string;
  details?: string;
  context?: string;
  timestamp?: string;
  stack?: string;
}

interface RecoveryAction {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  variant: "contained" | "outlined" | "text";
  color: "primary" | "secondary" | "error" | "warning" | "info";
}

interface ErrorPageProps {
  eventBus?: EventBus;
}

export const ErrorPage: React.FC<ErrorPageProps> = ({ eventBus }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [errorInfo, setErrorInfo] = useState<ErrorInfo>({
    message: "An unexpected error occurred",
    details: "The application encountered an error and could not continue.",
    context: "unknown",
  });

  useEffect(() => {
    // Extract error information from URL parameters
    const errorCode = searchParams.get("code");
    const errorMessage = searchParams.get("message");
    const errorDetails = searchParams.get("details");
    const errorContext = searchParams.get("context");
    const errorTimestamp = searchParams.get("timestamp");

    if (errorCode || errorMessage || errorDetails || errorContext) {
      setErrorInfo({
        code: errorCode || undefined,
        message: errorMessage || "An error occurred",
        details: errorDetails || "Please try again or contact support.",
        context: errorContext || "unknown",
        timestamp: errorTimestamp || new Date().toISOString(),
      });
    }

    // Emit error occurred event for tracking
    if (eventBus) {
      eventBus.emitGlobal(StateEvent.ERROR_OCCURRED, {
        error: errorMessage || "Unknown error",
        context: errorContext || "error_page",
        code: errorCode,
        timestamp: errorTimestamp || new Date().toISOString(),
      });
    }
  }, [searchParams, eventBus]);

  const handleRecover = () => {
    // Emit recover from error event
    if (eventBus) {
      eventBus.emitGlobal(StateEvent.RECOVER_FROM_ERROR, {
        timestamp: new Date().toISOString(),
        context: errorInfo.context,
      });
    }

    // Navigate to recovery phase
    void navigate(RECOVERY.path);
  };

  const handleGoHome = () => {
    // Navigate to organization selection so the user can establish scope.
    void navigate(ORGANIZATIONS.ROOT.path);
  };

  const handleGoToLogin = () => {
    // Navigate to login
    void navigate(AUTH.LOGIN.path);
  };

  const handleRefresh = () => {
    // Reload the page
    window.location.reload();
  };

  const handleReportBug = () => {
    // Open bug report (could be a modal or external link)
    const bugReportUrl = `mailto:support@rusha.com?subject=Bug Report: ${errorInfo.code || "Unknown Error"}&body=Error Details:%0D%0A%0D%0ACode: ${errorInfo.code || "N/A"}%0D%0AMessage: ${errorInfo.message}%0D%0AContext: ${errorInfo.context}%0D%0ATimestamp: ${errorInfo.timestamp}%0D%0A%0D%0APlease provide additional details about what you were doing when this error occurred.`;
    window.open(bugReportUrl, "_blank");
  };

  const getErrorSeverity = (code?: string): "error" | "warning" | "info" => {
    if (!code) return "error";

    // Determine severity based on error code
    if (code.startsWith("AUTH_") || code.startsWith("PERMISSION_")) {
      return "warning";
    } else if (code.startsWith("NETWORK_") || code.startsWith("TIMEOUT_")) {
      return "info";
    }

    return "error";
  };

  const getRecoveryActions = (context?: string): RecoveryAction[] => {
    const actions: RecoveryAction[] = [
      {
        label: "Try Again",
        icon: <RefreshIcon />,
        onClick: handleRefresh,
        variant: "contained",
        color: "primary",
      },
    ];

    // Add context-specific actions
    if (context === "auth" || context === "login") {
      actions.push({
        label: "Go to Login",
        icon: <LoginIcon />,
        onClick: handleGoToLogin,
        variant: "outlined",
        color: "primary",
      });
    } else if (context === "workspace" || context === "dashboard") {
      actions.push({
        label: "Go to Organizations",
        icon: <HomeIcon />,
        onClick: handleGoHome,
        variant: "outlined",
        color: "primary",
      });
    }

    // Add recovery action if not in auth context
    if (context !== "auth" && context !== "login") {
      actions.push({
        label: "Recover Session",
        icon: <RefreshIcon />,
        onClick: handleRecover,
        variant: "outlined",
        color: "secondary",
      });
    }

    return actions;
  };

  const severity = getErrorSeverity(errorInfo.code);
  const recoveryActions = getRecoveryActions(errorInfo.context);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        p: 3,
      }}
    >
      {/* Main Error Content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          maxWidth: 700,
          width: "100%",
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <ErrorIcon sx={{ fontSize: 64, color: "error.main", mb: 2 }} />
          <Typography variant="h3" component="h1" gutterBottom>
            Oops! Something went wrong
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            We encountered an error while processing your request
          </Typography>
        </Box>

        {/* Error Details Card */}
        <Card sx={{ width: "100%", mb: 4 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" component="h2">
                Error Details
              </Typography>
              {errorInfo.code && (
                <Chip
                  label={errorInfo.code}
                  size="small"
                  sx={{ ml: 2 }}
                  color={
                    severity === "error"
                      ? "error"
                      : severity === "warning"
                        ? "warning"
                        : "info"
                  }
                />
              )}
            </Box>

            <Alert severity={severity} sx={{ mb: 3 }}>
              <Typography variant="body1" fontWeight="medium">
                {errorInfo.message}
              </Typography>
              {errorInfo.details && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {errorInfo.details}
                </Typography>
              )}
            </Alert>

            {/* Error Information */}
            <List dense>
              {errorInfo.context && (
                <ListItem>
                  <ListItemIcon>
                    <ErrorIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Context"
                    secondary={errorInfo.context}
                  />
                </ListItem>
              )}
              {errorInfo.timestamp && (
                <ListItem>
                  <ListItemIcon>
                    <ErrorIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Timestamp"
                    secondary={new Date(errorInfo.timestamp).toLocaleString()}
                  />
                </ListItem>
              )}
            </List>

            {/* Stack Trace (Development Only) */}
            {import.meta.env.DEV && errorInfo.stack && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Stack Trace (Development)
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    bgcolor: "grey.100",
                    p: 2,
                    borderRadius: 1,
                    fontSize: "0.75rem",
                    overflow: "auto",
                    maxHeight: 200,
                  }}
                >
                  {errorInfo.stack}
                </Box>
              </>
            )}
          </CardContent>
        </Card>

        {/* Recovery Actions */}
        <Card sx={{ width: "100%", mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              What would you like to do?
            </Typography>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 2 }}>
              {recoveryActions.map((action, index) => (
                <Button
                  key={index}
                  variant={action.variant}
                  color={action.color}
                  startIcon={action.icon}
                  onClick={action.onClick}
                  size="large"
                >
                  {action.label}
                </Button>
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Support Information */}
        <Card sx={{ width: "100%" }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Need Help?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              If this error persists or you need assistance, please report the
              issue to our support team.
            </Typography>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<BugReportIcon />}
                onClick={handleReportBug}
                size="medium"
              >
                Report Bug
              </Button>

              <Button
                variant="text"
                onClick={() =>
                  window.open("https://rusha.com/support", "_blank")
                }
                size="medium"
              >
                Contact Support
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Error Code Reference */}
        {errorInfo.code && (
          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography variant="caption" color="text.secondary">
              Error Code: {errorInfo.code}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ErrorPage;
